import React, { useReducer, useState, useEffect } from 'react'
import { Grid, Row, Col } from 'react-bootstrap'
import SettingsSectionView from '../component/SettingsSectionView'
import ConfirmableCheckbox from '../../../../../../../Components/ConfirmableCheckbox/container/ConfirmableCheckbox'
import { authorization } from '../../../../../../../Module/Auth/Auth'
import { updateUserData } from '../../../../../../../Module/graphQL'
import { SelectDashboard } from '../../../../../../../Components/Form'
import {
  createOrUpdateLinkedSpy,
  getTeamLeaderSpiesData,
  updateRestrictedMissionType,
  updateUserSettings,
} from './gql'
import { BA_MISSIONS_LIMITS, MISSION_TYPES_OPTIONS } from './options'
import Note from '../../../../../../../Components/Note'
import { AmbassadorSection } from 'Components/City/Page/Ambassador/AmbassadorSection'
import { SectionTitle } from 'Components/Section/SectionTitle'
import SendWarningButton from '../../SendWarningButton/SendWarningButton'
import { createUserProfileLog } from '../../../gql'
import { USER_PROFILE_LOGS_TYPE } from '../../../../../../../constants'
import useGraphQLFetch from '../../../../../../../Utils/Hooks/useGraphQLFetch'
import { BRAND_AMBASSADOR_STATUS_ENUM } from '../../../../../../../shared/constants'
import InputLabelWithHint from '../../../../../../../Components/InputLabelWithHint'
import {
  hideReduxModal,
  showReduxModal,
} from '../../../../../../../Reducers/UI/actions'
import { connect } from 'react-redux'

const ACTIONS = {
  SET_IS_TL: 'SET_IS_TL',
  MERGE_UPDATED_SETTINGS: 'MERGE_UPDATED_SETTINGS',
}

const type = {
  ALL: 'MISSION_TYPE_ALL',
  HANDTOHAND: 'MISSION_TYPE_H2H',
  LETTERBOX: 'MISSION_TYPE_D2D',
}

const getInitialState = ({ isTeamLeader }) => ({
  isTeamLeader,
})

const reducer = (state, action) => {
  if (action.type === ACTIONS.SET_IS_TL) {
    return {
      ...state,
      isTeamLeader: action.value,
    }
  }
  if (action.type === ACTIONS.MERGE_UPDATED_SETTINGS) {
    return {
      ...state,
      ...action.value,
    }
  }
  return state
}

const NOT_SELECTED_SPY_OPTION = { label: 'None', value: 'NONE' }

const SettingsSection = ({
  ambassador = {},
  teamLeaderEnabled,
  logsParams,
  showReduxModal,
}) => {
  const {
    restrictedMissionType,
    id,
    city: { id: cityId },
    isTeamLeader,
  } = ambassador

  const defaultFetchArgsSpies = {
    cityId,
    statuses: BRAND_AMBASSADOR_STATUS_ENUM.ACTIVE,
    tlId: ambassador.id,
  }

  const [settings, settingsDispatch] = useReducer(
    reducer,
    getInitialState(ambassador),
  )
  const [loading, setLoading] = useState(false)
  const [spies, setSpies] = useState([])
  const [linkedSpyId, setLinkedSpyId] = useState(NOT_SELECTED_SPY_OPTION.value)
  const [linkedSpyIdSavedInDB, setLinkedSpyIdSavedInDB] = useState(
    NOT_SELECTED_SPY_OPTION.value,
  )
  const [errorMessage, setErrorMessage] = useState(null)
  const [userSettings, setUserSettings] = useState(ambassador.userSettings)

  const { data, refetch, isLoaded } = useGraphQLFetch({
    fetchFn: getTeamLeaderSpiesData,
    defaultFetchArgs: defaultFetchArgsSpies,
    fetchId: 'getTeamLeaderSpiesData',
    skipFetchOnInit: !isTeamLeader,
    mapResponse: data => {
      return { spies: data.city.spies.nodes, linkedSpy: data.getLinkedSpy }
    },
  })

  useEffect(() => {
    if (!isTeamLeader && settings.isTeamLeader && !spies.length) {
      refetch(defaultFetchArgsSpies)
    }
  }, [settings.isTeamLeader])

  useEffect(() => {
    if (isLoaded && data) {
      const spiesOptions = data.spies.map(spy => ({
        label: `${spy.firstName} ${spy.lastName}`,
        value: spy.id,
      }))

      setSpies([NOT_SELECTED_SPY_OPTION, ...spiesOptions])
      if (data.linkedSpy) {
        setSpies(spiesOptions)
        setLinkedSpyId(data.linkedSpy.id)
        setLinkedSpyIdSavedInDB(data.linkedSpy.id)
      }
    }
  }, [isLoaded])

  const toggleIsTL = () =>
    settingsDispatch({
      type: ACTIONS.SET_IS_TL,
      value: !settings.isTeamLeader,
    })

  const onSaveUserSettings = async () => {
    const { isValid } = validateSettingsSave()
    if (!isValid) return
    try {
      setLoading(true)
      const authToken = await authorization.getAPIKey()

      await updateUserSettings({
        userId: ambassador.id,
        tlaWeeklyMissionsLimit: userSettings.tlaWeeklyMissionsLimit,
        baWeeklyMissionsLimit: userSettings.baWeeklyMissionsLimit,
      })

      if (teamLeaderEnabled) {
        if (settings.isTeamLeader) {
          await createOrUpdateLinkedSpy({
            tlId: ambassador.id,
            spyId: linkedSpyId,
          })
        }

        const response = await updateUserData(
          authToken,
          ambassador.id,
          settings,
        )
        const {
          data: {
            updateUserData: { user },
          },
        } = await response.json()

        refetch(defaultFetchArgsSpies)
        settingsDispatch({
          type: ACTIONS.MERGE_UPDATED_SETTINGS,
          value: user.isTeamLeader,
        })
      }

      setLoading(false)
    } catch (error) {
      if (error.message) {
        setErrorMessage(error.message)
      }
      setLoading(false)
    }
  }

  const onChange = async (event, index, value) => {
    if (value && value !== restrictedMissionType) {
      const restrictedMissionType = value === 'ALL' ? null : value
      await updateRestrictedMissionType({
        id,
        restrictedMissionType,
      })

      await createUserProfileLog(
        logsParams(USER_PROFILE_LOGS_TYPE[type[value]]),
      )
    }
  }

  const validateSettingsSave = () => {
    if (settings.isTeamLeader) {
      if (linkedSpyId === NOT_SELECTED_SPY_OPTION.value) {
        showReduxModal({
          title: 'Warning',
          children: 'Select a corresponding spy account for this BA',
          onConfirm: hideReduxModal,
          hideCancel: true,
        })
        return { isValid: false }
      }
      if (
        linkedSpyId !== linkedSpyIdSavedInDB &&
        linkedSpyId !== NOT_SELECTED_SPY_OPTION.value &&
        linkedSpyIdSavedInDB !== NOT_SELECTED_SPY_OPTION.value
      ) {
        showReduxModal({
          title: 'Error',
          children: 'Dinamic spy account changing is not allowed',
          onConfirm: hideReduxModal,
          hideCancel: true,
        })
        return { isValid: false }
      }
    }
    return { isValid: true }
  }

  return (
    <Row>
      <Col xs={8}>
        <SettingsSectionView onSave={onSaveUserSettings} loading={loading}>
          <Row>
            <Col xs={12}>
              <InputLabelWithHint
                label="Max BA Missions per Week"
                hint="Select the maximum number of missions this BA can book per week. Select 'No Limit' for unrestricted booking."
                hintPosition="left"
              />
              <SelectDashboard
                options={BA_MISSIONS_LIMITS}
                onChange={(e, k, v) => {
                  setUserSettings(prev => ({
                    ...prev,
                    baWeeklyMissionsLimit: v,
                  }))
                }}
                input={{
                  value: userSettings.baWeeklyMissionsLimit,
                }}
              />
              <div
                style={{ fontSize: '12px', color: '#546b81', margin: '10px 0' }}
              >
                Editing this value will override the value set in Cities, only
                for this BA
              </div>
            </Col>
          </Row>
          <Col>
            {teamLeaderEnabled ? (
              <ConfirmableCheckbox
                disabled={!teamLeaderEnabled}
                label="Team Leader"
                value={settings.isTeamLeader}
                confirmationModal={{
                  title: 'Warning',
                  body: 'Are you sure?',
                  onConfirm: toggleIsTL,
                  showOnDisable: true,
                }}
                note={
                  teamLeaderEnabled
                    ? 'Be careful when linking and choose the correct spy account for this BA'
                    : null
                }
                noteStyles={{
                  fontSize: '12px',
                  color: '#546b81',
                  marginTop: '6px',
                }}
                cypress_id={'TLFeatureCheckbox'}
              />
            ) : (
              <Note>Team Leader feature is disabled in city settings</Note>
            )}
          </Col>
          {spies.length && settings.isTeamLeader ? (
            <Row style={{ marginTop: '10px' }}>
              <Col xs={6}>
                <SelectDashboard
                  label="Spy"
                  name="spy"
                  initValue={linkedSpyId}
                  options={spies}
                  onChange={(event, index, value) => {
                    setLinkedSpyId(value)
                    setErrorMessage(null)
                  }}
                />
                {errorMessage ? (
                  <Col style={{ color: 'red', marginTop: '6px' }}>
                    Not saved. {errorMessage}
                  </Col>
                ) : null}
              </Col>

              <Col xs={6}>
                <InputLabelWithHint
                  label="Max TLA Missions per Week"
                  hint="Select the maximum number of missions this TLA can book per week. Select 'No Limit' for unrestricted booking."
                  hintPosition="left"
                />
                <SelectDashboard
                  options={BA_MISSIONS_LIMITS}
                  onChange={(e, k, v) => {
                    setUserSettings(prev => ({
                      ...prev,
                      tlaWeeklyMissionsLimit: v,
                    }))
                  }}
                  input={{
                    value: userSettings.tlaWeeklyMissionsLimit,
                  }}
                />
                <div
                  style={{
                    fontSize: '12px',
                    color: '#546b81',
                    margin: '10px 0',
                  }}
                >
                  Editing this value will override the value set in Cities, only
                  for this TLA
                </div>
              </Col>
            </Row>
          ) : null}
        </SettingsSectionView>
      </Col>
      <Col xs={4} style={{ display: 'flex', flexDirection: 'column' }}>
        <AmbassadorSection className="settings">
          <Grid fluid>
            <Row>
              <Col>
                <SectionTitle>Mission Type</SectionTitle>
                <SelectDashboard
                  name="missionType"
                  initValue={restrictedMissionType || 'ALL'}
                  options={MISSION_TYPES_OPTIONS}
                  onChange={onChange}
                />
                <div
                  style={{
                    fontSize: '12px',
                    color: '#546b81',
                    margin: '64px 0px 10px',
                  }}
                >
                  BA will only see the missions with the selected type
                </div>
              </Col>
            </Row>
          </Grid>
        </AmbassadorSection>
        <AmbassadorSection className="settings">
          <Grid fluid>
            <Row>
              <Col style={{ display: 'flex', flexDirection: 'column' }}>
                <SectionTitle>Send Warning</SectionTitle>
                <SendWarningButton
                  recipientId={ambassador.id}
                  count={ambassador.userWarning && ambassador.userWarning.count}
                />
              </Col>
            </Row>
          </Grid>
        </AmbassadorSection>
      </Col>
    </Row>
  )
}

export default connect(
  null,
  {
    showReduxModal,
  },
)(SettingsSection)
